import { KrispAudioProcessor } from '../krispAudioProcessor';

/**
 * The microphone for one call, run through Krisp when the browser can.
 *
 * Exists for carriers whose SDK takes a ready-made `localStream` instead of
 * letting us insert a processor into their own capture (Twilio's
 * `device.audio.addProcessor` does that part itself).
 */
export interface MicPipeline {
  /** What the carrier sends: Krisp-processed, or the raw mic when Krisp can't run. */
  readonly stream: MediaStream;
  isNoiseCancellationEnabled(): boolean;
  /** Flips Krisp's in-node bypass. No renegotiation. A no-op without Krisp. */
  toggleNoiseCancellation(): void;
  /** Releases the mic tracks, the Krisp node and the AudioContext. Idempotent. */
  close(): void;
}

// Browser echo cancellation only works on the raw mic track, so it has to be
// requested here, before Krisp. noiseSuppression is left at the browser
// default so a call that falls back to the raw mic still gets it.
const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { echoCancellation: true, autoGainControl: true },
};

export async function openKrispMicPipeline(): Promise<MicPipeline> {
  // A denied permission rejects here, before anything is allocated.
  const mic = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);

  const ctx = new AudioContext();
  const krisp = new KrispAudioProcessor();
  let processed: MediaStream | null = null;

  try {
    // A context created outside a user gesture can start suspended and would
    // then emit silence.
    if (ctx.state === 'suspended') await ctx.resume();
    const source = ctx.createMediaStreamSource(mic);
    const node = await krisp.createProcessingNode(ctx, source);
    if (node) {
      const destination = ctx.createMediaStreamDestination();
      node.connect(destination);
      processed = destination.stream;
    }
  } catch {
    // Krisp failing to load must not cost the agent the call: fall through to
    // the raw mic, the same outcome as a browser Krisp does not support.
    krisp.stopProcessingNode();
    processed = null;
  }

  if (!processed) {
    void ctx.close().catch(() => {});
  }

  let closed = false;
  const active = processed !== null;

  return {
    stream: processed ?? mic,
    isNoiseCancellationEnabled: () => !closed && active && krisp.isEnabled(),
    toggleNoiseCancellation: () => {
      if (!closed && active) krisp.toggle();
    },
    close: () => {
      if (closed) return;
      closed = true;
      krisp.stopProcessingNode();
      // Stopping the raw track is what turns the browser's mic indicator off;
      // the SDK only stops the tracks of the stream it was given.
      mic.getTracks().forEach(track => track.stop());
      processed?.getTracks().forEach(track => track.stop());
      if (active) void ctx.close().catch(() => {});
    },
  };
}
